import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Header from "../Header";
import Field from "../../../../../Settings/Content/Components/Field";
import TrashBucketIcon from "../../../../../../Components/Icon-SVG/TrashBucketIcon";
import Tooltip from "../../../../../../Components/common/Tooltip/Tooltip";
import Backdrop from "../../../../../../Components/UI/Backdrop/Backdrop";

import { RequestUpdateAgentPolicy } from "../../../../../../store/actions/integrationsActions";
import { addNotification } from "../../../../../../store/actions/notifierActions";

const MIN_MAX_VALUES = {
  scanTimeoutMinutes: [5, 120],
  defaultDepth: [1, 10],
  usersDepth: [1, 10],
  eventLogLookbackDays: [1, 90],
};

const SCALAR_FIELDS = [
  "memoryScanEnabled",
  "scanTimeoutMinutes",
  "defaultDepth",
  "usersDepth",
  "eventLogLookbackDays",
];
const BOOL_FIELDS = ["enabled", "memoryScanEnabled"];
const CSV_FIELDS = ["fileExtensions"];

const toBool = (v) => v === true || v === "true" || v === "True";
const toArray = (v) =>
  typeof v === "string"
    ? v
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : Array.isArray(v)
      ? v
      : [];

const THunterForm = ({ selectedHost }) => {
  const dispatch = useDispatch();

  const policy = useSelector((state) => state.integrations.agentPolicy);
  const updatePolicyLoading = useSelector((state) => state.integrations.updatePolicyLoading);

  const backend = policy?.settings?.thunter;

  const [thunterUI, setThunterUI] = useState({
    ...backend,
    memoryScanEnabled: toBool(backend?.memoryScanEnabled),
    fileExtensions: toArray(backend?.fileExtensions),
  });
  const [buffers, setBuffers] = useState({ fileExtensions: "" });

  const numberChangeHandler = (e) => {
    const { name, value } = e.target;
    setThunterUI((prev) => ({ ...prev, [name]: value === "" ? "" : Number(value) }));
  };

  const boolChangeHandler = (e) => {
    const { name, value } = e.target;
    setThunterUI((prev) => ({ ...prev, [name]: value === "true" }));
  };

  const blurHandler = (e) => {
    const { name, value } = e.target;
    const limits = MIN_MAX_VALUES[name];
    if (!limits) return;

    const [min, max] = limits;
    let newValue = Number(value);

    if (newValue < min) newValue = min;
    if (newValue > max) newValue = max;

    setThunterUI((prev) => ({ ...prev, [name]: newValue }));
  };

  const bufferChangeHandler = (name, value) => {
    setBuffers((prev) => ({ ...prev, [name]: value }));
  };

  const addCsvItem = (name) => {
    const item = (buffers[name] || "").trim();

    if (item === "") {
      dispatch(
        addNotification({
          message: "Enter a valid value",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }
    if ((thunterUI[name] || []).some((existing) => existing === item)) {
      dispatch(
        addNotification({
          message: "Value already exists",
          options: { variant: "error" },
          time: 3000,
        }),
      );
      return;
    }

    setThunterUI((prev) => ({ ...prev, [name]: [...(prev[name] || []), item] }));
    setBuffers((prev) => ({ ...prev, [name]: "" }));
  };

  const removeCsvItem = (name, index) => {
    setThunterUI((prev) => ({ ...prev, [name]: prev[name].filter((_, i) => i !== index) }));
  };

  const checkSaveButtonDisabled = () => {
    if (!backend || !thunterUI) return true;

    for (const f of SCALAR_FIELDS) {
      if (BOOL_FIELDS.includes(f)) {
        if (toBool(backend[f]) !== thunterUI[f]) return false;
      } else if (backend[f] !== thunterUI[f]) {
        return false;
      }
    }

    for (const f of CSV_FIELDS) {
      const backendArr = toArray(backend[f]).slice().sort();
      const uiArr = [...(thunterUI[f] || [])].sort();
      if (backendArr.length !== uiArr.length) return false;
      if (!backendArr.every((v, i) => v === uiArr[i])) return false;
    }

    return true;
  };

  const updateAgentSettingsHandler = (e) => {
    e.preventDefault();
    if (updatePolicyLoading) return;

    dispatch(
      RequestUpdateAgentPolicy({
        id: policy.id,
        settings: {
          ...policy.settings,
          thunter: {
            ...thunterUI,
            fileExtensions: thunterUI.fileExtensions.join(","),
          },
        },
        host: selectedHost,
      }),
    );
  };

  const toggleStatusHandler = () => {
    dispatch(
      RequestUpdateAgentPolicy({
        id: policy.id,
        settings: {
          ...policy.settings,
          thunter: {
            ...policy.settings.thunter,
            enabled: !policy?.settings?.thunter?.enabled,
          },
        },
        host: selectedHost,
      }),
    );
  };

  const renderBool = (name, header, tooltip) => (
    <div className="agent-policy-form__line">
      <div className="mailing__content__security m-b-20">
        <p className="light m-b-10 mailing__content__security__header">
          {header}
          <Tooltip text={tooltip} />
        </p>
        <div className="mailing__content__security__labels">
          <label className="align-items">
            <input
              className="mailing__content__security__input"
              type="radio"
              value={true}
              name={name}
              checked={thunterUI[name] === true}
              onChange={boolChangeHandler}
            />
            <p className="margin-left-12">Enabled</p>
          </label>
          <label className="align-items">
            <input
              className="mailing__content__security__input"
              type="radio"
              value={false}
              name={name}
              checked={thunterUI[name] === false}
              onChange={boolChangeHandler}
            />
            <p className="margin-left-12">Disabled</p>
          </label>
        </div>
      </div>
    </div>
  );

  const renderCsvList = (name, label, tooltip, placeholder) => (
    <>
      <div className="agent-policy-form__group-line">
        <Field
          label={label}
          tooltipText={tooltip}
          width="300px"
          value={buffers[name]}
          onChange={(e) => bufferChangeHandler(name, e.target.value)}
          parentClassName="p-r-20"
          placeholder={placeholder}
        />
        <button
          type="button"
          className="products-body-form__button"
          onClick={() => addCsvItem(name)}>
          +
        </button>
      </div>

      <ul className="agent-policy-form__list" style={{ gridColumn: "span 2" }}>
        {thunterUI?.[name]?.length > 0 ? (
          thunterUI[name].map((item, index) => (
            <li key={index} className="agent-policy-form__list-item">
              {item}
              <button
                type="button"
                style={{ display: "flex" }}
                onClick={() => removeCsvItem(name, index)}>
                <TrashBucketIcon width="16" height="16" />
                <p style={{ marginLeft: "5px" }}>Delete</p>
              </button>
            </li>
          ))
        ) : (
          <li className="no-data">&nbsp;No Data</li>
        )}
      </ul>
    </>
  );

  return (
    <>
      <Header
        policy={policy}
        selectedHost={selectedHost}
        toggleProps={{
          show: true,
          text: "Threat Hunter Status",
          active: policy?.settings?.thunter?.enabled,
          onClick: toggleStatusHandler,
        }}
      />

      <form className="agent-policy-form">
        {renderBool(
          "memoryScanEnabled",
          "Memory Scan",
          "If enabled, running processes' memory is scanned during the deep scan.",
        )}

        <div className="agent-policy-form__line">
          <Field
            label="Scan Timeout in minutes"
            tooltipText="Min: 5, Max: 120"
            width="220px"
            name="scanTimeoutMinutes"
            value={thunterUI.scanTimeoutMinutes}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
          <Field
            label="Event Log Lookback in days"
            tooltipText="Defender event-log window. Min: 1, Max: 90"
            width="220px"
            name="eventLogLookbackDays"
            value={thunterUI.eventLogLookbackDays}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
        </div>

        <div className="agent-policy-form__line">
          <Field
            label="Default Depth"
            tooltipText="Folder recursion depth for general paths. Min: 1, Max: 10"
            width="220px"
            name="defaultDepth"
            value={thunterUI.defaultDepth}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
          <Field
            label="Users Depth"
            tooltipText="Folder recursion depth under \\Users. Min: 1, Max: 10"
            width="220px"
            name="usersDepth"
            value={thunterUI.usersDepth}
            onChange={numberChangeHandler}
            onBlur={blurHandler}
            type="number"
          />
        </div>

        {renderCsvList(
          "fileExtensions",
          "File Extension",
          "File types eligible for the deep scan",
          "Enter an extension (e.g., exe, dll, pdf)",
        )}

        <button
          className="primary-button"
          disabled={checkSaveButtonDisabled()}
          onClick={updateAgentSettingsHandler}>
          Save
        </button>

        <Backdrop
          spinner={false}
          absolute
          show={!policy?.settings?.thunter?.enabled}
          message="Threat hunter settings are inactive! Please click on the status button at the header to activate threat hunter settings."
          messageStyle={{ paddingTop: 0 }}
        />
      </form>
    </>
  );
};

export default THunterForm;
